/**
 * Login Page — Impact Core
 *
 * Minimal, professional login form.
 * Calls AuthContext.login() and redirects to dashboard on success.
 */

import { useState, type FormEvent } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { LogIn, AlertCircle, Loader2 } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { env } from '@/config/env';

export function LoginPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { login } = useAuth();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Redirect target after login (default: dashboard)
  const from = (location.state as { from?: string })?.from ?? '/';

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setIsSubmitting(true);

    try {
      await login(email, password);
      navigate(from, { replace: true });
    } catch (err: unknown) {
      const message = parseLoginError(err);
      setError(message);
    } finally {
      setIsSubmitting(false);
    }
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-ic-surface px-4">
      <div className="w-full max-w-sm">
        {/* Branding */}
        <div className="mb-8 text-center">
          <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-xl bg-ic-accent font-bold text-white text-lg">
            IC
          </div>
          <h1 className="text-xl font-semibold text-ic-text-primary tracking-tight">
            {env.appName}
          </h1>
          <p className="mt-1 text-sm text-ic-text-tertiary">
            Institutional Impact Investment Platform
          </p>
        </div>

        {/* Login Form */}
        <form onSubmit={handleSubmit} className="space-y-4">
          {error && (
            <div className="flex items-start gap-2 rounded-md border border-red-500/25 bg-red-500/10 px-3 py-2.5 text-sm text-red-400">
              <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
              <span>{error}</span>
            </div>
          )}

          <div>
            <label
              htmlFor="email"
              className="mb-1.5 block text-xs font-medium text-ic-text-secondary"
            >
              Email
            </label>
            <input
              id="email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              autoComplete="email"
              autoFocus
              className="w-full rounded-md border border-ic-border bg-ic-input px-3 py-2 text-sm text-ic-text-primary placeholder:text-ic-text-tertiary outline-none focus:border-ic-accent focus:ring-1 focus:ring-ic-accent/50 transition-colors"
              placeholder="you@company.com"
            />
          </div>

          <div>
            <label
              htmlFor="password"
              className="mb-1.5 block text-xs font-medium text-ic-text-secondary"
            >
              Password
            </label>
            <input
              id="password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              autoComplete="current-password"
              className="w-full rounded-md border border-ic-border bg-ic-input px-3 py-2 text-sm text-ic-text-primary placeholder:text-ic-text-tertiary outline-none focus:border-ic-accent focus:ring-1 focus:ring-ic-accent/50 transition-colors"
              placeholder="Enter password"
            />
          </div>

          <button
            type="submit"
            disabled={isSubmitting}
            className="flex w-full items-center justify-center gap-2 rounded-md bg-ic-accent px-4 py-2.5 text-sm font-medium text-white hover:bg-ic-accent-hover disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            {isSubmitting ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <LogIn className="h-4 w-4" />
            )}
            {isSubmitting ? 'Signing in...' : 'Sign In'}
          </button>
        </form>

        {/* Footer */}
        <p className="mt-6 text-center text-[10px] text-ic-text-tertiary">
          Powered by Society Intelligence Platform
        </p>
      </div>
    </div>
  );
}

// ─── Helpers ───────────────────────────────────────────────────

function parseLoginError(err: unknown): string {
  if (typeof err === 'object' && err !== null && 'response' in err) {
    const response = (err as { response?: { data?: { detail?: unknown }; status?: number } }).response;
    const detail = response?.data?.detail;

    if (typeof detail === 'string') return detail;

    if (Array.isArray(detail)) {
      return detail
        .map((d: { msg?: string }) => d.msg)
        .filter(Boolean)
        .join(', ');
    }

    if (response?.status === 401) return 'Invalid email or password.';
    if (response?.status === 422) return 'Please check your input and try again.';
    if (response?.status === 429) return 'Too many attempts. Please wait and try again.';
  }

  return 'Unable to sign in. Please check your connection and try again.';
}
